import { useState, useEffect, useContext, useCallback, useRef } from "react";
import axiosInstance from "../api/axios";
import { Link } from "react-router-dom";
import { useAppSocket } from "../hooks/useAppSocket";
import { useChatThread } from "../hooks/useChatThread";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { Ellipsis, Send, Paperclip, Video as VideoIcon, X, Loader2 } from "lucide-react";
import AuthContext from "../context/AuthContext";
import { fetchUserProfile, fetchUserProfilesBulk } from "../api/UserAPI";
import {
  fetchThreadDetails,
  updateThreadTitle,
  markThreadRead,
  uploadMessageImage,
  finalizeMessageVideo,
  pollMessageVideoUntilReady,
} from "../api/ChatThreadsAPI";
// S3 presign + direct-upload are generic (not post-specific) — reused as-is.
import { getPresign, uploadToS3 } from "../api/PostAPI";
import { useModal } from "./ModalManager";
import { InlineEdit } from "./InlineEditor";

// Keep in sync with backend MessageImageSerializer.validate_image
const MAX_IMAGE_BYTES = 15 * 1024 * 1024; // 15 MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Message videos are production/S3-only (no local-storage dev path).
const MAX_VIDEO_BYTES = 100 * 1024 * 1024; // 100 MB (backend presign hard cap is 1GB)
const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];

export default function Conversation({ threadId, eventId }) {
  const { token, user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [partnerProfile, setPartnerProfile] = useState({});
  const messagesEndRef = useRef(null);
  const [nextUrl, setNextUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [lastFetchedTime, setLastFetchTime] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [hasFetched, setHasFetched] = useState(null);
  const [hasScrolledOnce, setHasScrolledOnce] = useState(false);
  const [defaultTitle, setDefaultTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const { openModal } = useModal();
  const FETCH_DELAY = 3000;

  // Shared attachment control — one file input/button routes to whichever
  // pipeline (image or video) matches the selected file's MIME type. Each
  // pipeline still owns its own state below; this button just picks one.
  const attachmentInputRef = useRef(null);
  const [attachmentError, setAttachmentError] = useState("");

  // Image attachment state
  const [selectedImage, setSelectedImage] = useState(null); // File
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null); // object URL
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");

  // Video attachment state
  const [selectedVideo, setSelectedVideo] = useState(null); // File
  // "idle" | "uploading" | "processing" | "error"
  const [videoUploadState, setVideoUploadState] = useState("idle");
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoError, setVideoError] = useState("");
  // Bumped on every new attempt/removal so stale async work (from a canceled
  // or superseded upload) can't clobber state for a later attempt.
  const videoAttemptRef = useRef(0);
  const isVideoBusy = videoUploadState === "uploading" || videoUploadState === "processing";

  const messageContainerRef = useRef(null);

  const [profiles, setProfiles] = useState([]);
  const [partner, setPartner] = useState(null);
  const [threadDetail, setThreadDetail] = useState({});

  // init load
  useEffect(() => {
    if (!threadId || !token) return;

    let canceled = false;
    const ac = new AbortController();

    (async () => {
      try {
        const details = await fetchThreadDetails(threadId, token, { signal: ac.signal });
        if (canceled || ac.signal.aborted) return;
        // setThreadDetail(Array.isArray(details) ? details : details?.results ?? []);
        setThreadDetail(details || {});

        if (details && Object.keys(details).length !== 0) {
          const participants = details.participants;
          const usernames = [];

          for (let i = 0; i < participants.length; i++) {
            usernames.push(participants[i].username);
          }
          const profiles = await fetchUserProfilesBulk(usernames);
          setProfiles(profiles);

          if (details.is_group) {
            // logic to get all profiles
            if (!details.title) {
              if (participants.length >= 3) {
                setDefaultTitle(
                  `${participants[0].username}, ${participants[1].username}, +others`
                );
              } else if (participants.length === 2) {
                setDefaultTitle(`${participants[0].username}, ${participants[1].username}`);
              } else if (participants.length === 1) {
                setDefaultTitle(participants[0].username);
              }
            }
          } else {
            const partnerName = participants[0].username;
            const partnerProfile = await fetchUserProfile(partnerName, token, {
              signal: ac.signal,
            });
            setPartner(partnerName);
            setPartnerProfile(partnerProfile);
          }
        }
      } catch (err) {
        if (!canceled) {
          console.error(err);
        }
      }
    })();

    return () => {
      canceled = true;
      ac.abort();
    };
  }, [threadId, token]);

  // BEGIN MESSAGE LOAD BEHAVIOR

  useEffect(() => {
    if (!threadId) return;

    setNextUrl(`/chat/threads/${threadId}/messages/`);
    setMessages([]);
    setPage(0);
    setLastFetchTime(0);
    setHasFetched(true);
    setHasMore(true);
    setHasScrolledOnce(false);
  }, [threadId]); // Once the threadId changes the state has to be reset

  const handleLoadMessage = useCallback(() => {
    const now = Date.now();
    if (!nextUrl || loading || now - lastFetchedTime < FETCH_DELAY) return; // sets limit on requests
    if (hasMore && hasFetched) {
      setPage((prev) => prev + 1);
      setHasFetched(false);
    }
  });

  // AUTO - SCROLL
  useEffect(() => {
    if (!hasScrolledOnce && messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      setHasScrolledOnce(true);
    }
  }, [messages.length, hasScrolledOnce]); // only fires once after messages populate, resets on partner change
  // END

  const debouncedRef = useRef(null);
  const debouncedNextPage = () => {
    if (debouncedRef.current) return;

    debouncedRef.current = setTimeout(() => {
      setPage((prev) => prev + 1);
      debouncedRef.current = null;
    }, 500);
  };

  const loaderRef = useInfiniteScroll(handleLoadMessage);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // GET MESSAGES
  useEffect(() => {
    const fetchThread = async () => {
      if (!nextUrl || loading) return;
      setLoading(true);
      try {
        const res = await axiosInstance.get(nextUrl, {
          headers: { Authorization: `Token ${token}` },
          // params: { limit: 100 },
        });
        setMessages((prev) => [...res.data.results.reverse().map(normalizeMessage), ...prev]);
        // setNextUrl(toRelative(res.data.next));
        setNextUrl(res.data.next);
        setHasMore(!!res.data.next);
      } catch (err) {
        console.error("Error fetching conversation messages", err);
      } finally {
        setHasFetched(true);
        setLastFetchTime(Date.now()); // updates fetch time
        setLoading(false);
      }
    };

    fetchThread();
  }, [page]);

  // Marks thread as read
  useEffect(() => {
    if (!threadId) return;
    markThreadRead(threadId, token);
  }, [threadId, messages.length]);

  // Probably going to need some enhanced protection tbh
  function formatMessage(text) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  }

  function normalizeMessage(msg) {
    return {
      id: msg.id || `ws-${Date.now()}`,
      content: msg.content || msg.message,
      sender: msg.sender,
      created_at: msg.timestamp || Date.now(),
      message_type: msg.message_type || "text",
      image_url: msg.image_url || msg.media_asset_url || null,
      image_asset_id: msg.image_asset_id || null,
      video_url: msg.video_url || null,
      video_asset_id: msg.video_asset_id || null,
    };
  }

  const handleIncomingMessage = useCallback((msg) => {
    setMessages((prev) => {
      const updated = [...prev, normalizeMessage(msg)];
      // Scroll after DOM update
      setTimeout(scrollToBottom, 0);
      return updated;
    });
  }, []);

  const { wsRef, status } = useAppSocket();
  const { sendMessage } = useChatThread(wsRef, status, handleIncomingMessage, {
    threadId,
  });

  // Revoke the object URL whenever the selected image changes/unmounts
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  const handleAttachmentClick = () => {
    attachmentInputRef.current?.click();
  };

  // Single entry point for the shared attachment input. Detects media type
  // from the file's MIME type and routes to the matching pipeline below —
  // it doesn't own any upload logic itself.
  const handleAttachmentSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;

    setAttachmentError("");

    if (file.type.startsWith("image/")) {
      handleImageFile(file);
    } else if (file.type.startsWith("video/")) {
      handleVideoFile(file);
    } else {
      setAttachmentError("Unsupported file type. Please choose an image or video.");
    }
  };

  const handleImageFile = (file) => {
    setImageError("");

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError("Only JPEG, PNG, or WebP images are supported.");
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setImageError("Image must be 15 MB or smaller.");
      return;
    }

    // Image and video attachments are mutually exclusive (backend only
    // supports one media asset per message).
    handleRemoveVideo();

    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setSelectedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setSelectedImage(null);
    setImagePreviewUrl(null);
    setImageError("");
  };

  const handleVideoFile = (file) => {
    setVideoError("");

    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      setVideoError("Only MP4, MOV, or WebM videos are supported.");
      return;
    }

    if (file.size > MAX_VIDEO_BYTES) {
      setVideoError("Video must be 100 MB or smaller.");
      return;
    }

    handleRemoveImage();
    handleRemoveVideo();
    setSelectedVideo(file);
  };

  const handleRemoveVideo = () => {
    // Invalidate any in-flight upload/finalize/poll for the current attempt
    // so a late response can't resurrect a removed/canceled attachment.
    videoAttemptRef.current += 1;
    setSelectedVideo(null);
    setVideoUploadState("idle");
    setVideoProgress(0);
    setVideoError("");
  };

  const handleSendVideo = async (caption) => {
    const attempt = ++videoAttemptRef.current;
    const file = selectedVideo;

    setVideoError("");
    setVideoUploadState("uploading");
    setVideoProgress(0);

    try {
      const presign = await getPresign(file, token);

      await uploadToS3({
        upload_url: presign.upload_url,
        headers: presign.headers,
        file,
        onProgress: (pct) => {
          if (videoAttemptRef.current === attempt) setVideoProgress(pct);
        },
      });
      if (videoAttemptRef.current !== attempt) return; // canceled/removed mid-upload

      const { id: videoAssetId } = await finalizeMessageVideo({ key: presign.key, file }, token);
      if (videoAttemptRef.current !== attempt) return;

      setVideoUploadState("processing");
      await pollMessageVideoUntilReady(videoAssetId, token);
      if (videoAttemptRef.current !== attempt) return;

      const sent = sendMessage(caption, {
        messageType: "video",
        videoAssetId,
      });

      if (!sent) {
        throw new Error("Unable to send message. Check your connection and try again.");
      }

      setNewMsg("");
      handleRemoveVideo();
    } catch (err) {
      console.error("Failed to send video message", err);
      if (videoAttemptRef.current === attempt) {
        setVideoError("Failed to send video. Please try again.");
        setVideoUploadState("error");
      }
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (isUploadingImage || isVideoBusy) return; // prevent duplicate sends while busy

    const trimmed = newMsg.trim();

    if (!trimmed && !selectedImage && !selectedVideo) return;

    // Video (optionally with caption) path.
    if (selectedVideo) {
      await handleSendVideo(trimmed);
      return;
    }

    // Text-only path: unchanged from existing behavior.
    if (!selectedImage) {
      sendMessage(trimmed);
      setNewMsg("");
      return;
    }

    // Image (optionally with caption) path.
    setImageError("");
    setIsUploadingImage(true);
    try {
      const asset = await uploadMessageImage(selectedImage, token);
      const sent = sendMessage(trimmed, {
        messageType: "image",
        mediaAssetId: asset.id,
      });

      if (!sent) {
        throw new Error("Unable to send message. Check your connection and try again.");
      }

      setNewMsg("");
      handleRemoveImage();
    } catch (err) {
      console.error("Failed to send image message", err);
      setImageError("Failed to send image. Please try again.");
      // Keep the selected image/preview so the user can retry.
    } finally {
      setIsUploadingImage(false);
    }
  };

  function handleOnEdit() {
    setIsEditing(true);
  }

  return (
    <div className="flex flex-col h-full">
      {/* Conversation HEAD */}
      <div className="flex border-b border-gray-500 mb-4 items-start">
        {threadDetail?.is_group ? (
          <div className="flex w-1/2">
            <img
              src={partnerProfile.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`}
              alt="profile pic"
              className="w-8 h-8 rounded-full object-cover border border-black flex-shrink-0"
            />
            <InlineEdit
              value={threadDetail.title ? threadDetail.title || defaultTitle : partner}
              // permissions for who can edit title
              canEdit={isEditing}
              onSave={async (newTitle) => {
                threadDetail.title = newTitle;
                await updateThreadTitle(threadId, newTitle, token);
              }}
              renderDisplay={() =>
                threadDetail.title ? threadDetail.title || defaultTitle : partner
              }
              className="text-xl font-bold mb-4 ml-2"
              showIcon={isEditing}
            />
          </div>
        ) : (
          <div className="flex w-1/2">
            <Link to={`/profiles/${partner}`} className="flex">
              <img
                src={partnerProfile.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`}
                alt="profile pic"
                className="w-8 h-8 rounded-full object-cover border border-black flex-shrink-0"
              />
              <InlineEdit
                value={threadDetail.title ? threadDetail.title || defaultTitle : partner}
                // permissions for who can edit title
                canEdit={isEditing}
                onSave={async (newTitle) => {
                  threadDetail.title = newTitle;
                  await updateThreadTitle(threadId, newTitle, token);
                }}
                renderDisplay={() =>
                  threadDetail.title ? threadDetail.title || defaultTitle : partner
                }
                className="text-xl font-bold mb-4 ml-2"
                showIcon={isEditing}
              />
            </Link>
          </div>
        )}

        <div className="flex w-1/2 justify-end">
          {isEditing ? (
            <button
              className={[
                "px-3 py-1",
                "border border-gray-300",
                "rounded-full",
                "text-sm font-medium",
                "bg-white",
                "hover:bg-gray-100 hover:border-gray-400",
                "transition-colors",
                "shadow-sm",
              ].join(" ")}
              onClick={() => setIsEditing(false)}
            >
              Done
            </button>
          ) : (
            <button
              onClick={() =>
                openModal("ConversationOptionsModal", {
                  token: token,
                  threadId: threadId,
                  onEdit: handleOnEdit,
                })
              }
            >
              {" "}
              <Ellipsis />
            </button>
          )}
        </div>
      </div>

      {/* Messages list */}
      <div
        ref={messageContainerRef}
        className="flex-1 space-y-2 overflow-y-auto border rounded p-2"
      >
        {nextUrl && <div className="h-[1px]" ref={loaderRef} />}
        {/* {messages.map((msg, idx) => (
          <div key={idx}>
            {msg.sender.username != user.username && threadDetail.is_group && (
              <p className="text-xs text-gray-500">{msg.sender.username}</p>
            )}
            {msg.sender.username != user.username && (
              <img
                src={profiles.(msg.sender.username).profile_pic || `${process.env.PUBLIC_URL}/logo512.png`}
                alt="profile pic"
                className="w-8 h-8 rounded-full object-cover border border-black"
              />
            )}
            <div
              className={`p-2 rounded-lg max-w-xs ${
                msg.sender.username === user.username ? "bg-blue-300 ml-auto" : "bg-gray-300"
              }`}
            >
              <p className="text-sm">{formatMessage(msg.content)}</p>
              <p className="text-xs text-gray-500">
                {new Date(msg.created_at).toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))} */}
        {messages.map((msg, idx) => {
          const uname = msg?.sender?.username;
          const avatar =
            profiles?.[uname]?.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`;

          return (
            <div key={idx} className="flex items-start gap-2 mb-3">
              {/* Left-side avatar for other users */}
              {uname !== user.username && (
                <img
                  src={avatar}
                  alt={`${uname} avatar`}
                  className="w-8 h-8 rounded-full object-cover border border-black flex-shrink-0"
                />
              )}

              <div className={`max-w-xs ${uname === user.username ? "ml-auto" : ""}`}>
                {uname !== user.username && threadDetail.is_group && (
                  <p className="text-[11px] text-gray-500 leading-none mb-1">{uname}</p>
                )}
                <div
                  className={`p-2 rounded-lg ${
                    uname === user.username ? "bg-blue-300" : "bg-gray-300"
                  }`}
                >
                  {msg.message_type === "image" && (msg.image_url || msg.media_asset_url) && (
                    <img
                      src={msg.image_url || msg.media_asset_url}
                      alt="Sent attachment"
                      className={`rounded-md max-w-full max-h-64 object-contain ${
                        msg.content ? "mb-2" : ""
                      }`}
                    />
                  )}
                  {msg.message_type === "video" &&
                    (msg.video_url ? (
                      <video
                        src={msg.video_url}
                        controls
                        preload="metadata"
                        className={`rounded-md max-w-full max-h-64 ${msg.content ? "mb-2" : ""}`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 rounded-md bg-gray-100 px-3 py-4 text-xs text-gray-600 ${
                          msg.content ? "mb-2" : ""
                        }`}
                      >
                        <Loader2 size={14} className="animate-spin" />
                        Video processing…
                      </div>
                    ))}
                  {msg.content && <p className="text-sm">{formatMessage(msg.content)}</p>}
                  <p className="text-[10px] text-gray-600 mt-1">
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div className="h-[1px]" ref={messagesEndRef} />
      </div>
      {/* Conversation input */}
      <div className="mt-2">
        {imagePreviewUrl && (
          <div className="flex items-center gap-2 mb-2">
            <div className="relative">
              <img
                src={imagePreviewUrl}
                alt="Selected attachment preview"
                className="h-16 w-16 rounded-md object-cover border border-gray-300"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                disabled={isUploadingImage}
                aria-label="Remove selected image"
                className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-0.5 disabled:opacity-50"
              >
                <X size={14} />
              </button>
            </div>
            {isUploadingImage && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Loader2 size={14} className="animate-spin" /> Uploading...
              </span>
            )}
          </div>
        )}
        {imageError && <p className="text-xs text-red-600 mb-1">{imageError}</p>}
        {selectedVideo && (
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-2 border border-gray-300 rounded-md px-2 py-1 max-w-full">
              <VideoIcon size={16} className="text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-700 truncate max-w-[10rem]">
                {selectedVideo.name}
              </span>
              {videoUploadState === "uploading" && (
                <span className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                  <Loader2 size={14} className="animate-spin" /> {videoProgress}%
                </span>
              )}
              {videoUploadState === "processing" && (
                <span className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                  <Loader2 size={14} className="animate-spin" /> Processing…
                </span>
              )}
              <button
                type="button"
                onClick={handleRemoveVideo}
                aria-label="Remove selected video"
                className="text-gray-500 hover:text-gray-800 flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}
        {videoError && <p className="text-xs text-red-600 mb-1">{videoError}</p>}
        {attachmentError && <p className="text-xs text-red-600 mb-1">{attachmentError}</p>}
        <form onSubmit={handleSend} className="mt-4 flex space-x-2">
          <input
            ref={attachmentInputRef}
            type="file"
            accept={[...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES].join(",")}
            onChange={handleAttachmentSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleAttachmentClick}
            disabled={isUploadingImage || isVideoBusy}
            aria-label="Attach photo or video"
            className="px-3 py-2 rounded-full border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
          >
            <Paperclip size={18} />
          </button>
          <input
            type="text"
            className="flex-1 border rounded px-3 py-2"
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
            placeholder="Type a message"
          />
          <button
            type="submit"
            disabled={
              isUploadingImage ||
              isVideoBusy ||
              (!newMsg.trim() && !selectedImage && !selectedVideo)
            }
            className="bg-blue-500 text-white px-4 py-2 rounded-full disabled:opacity-50"
          >
            {isUploadingImage || isVideoBusy ? <Loader2 className="animate-spin" /> : <Send />}
          </button>
        </form>
      </div>
    </div>
  );
}
